import { isAxiosError } from 'axios';
import { api } from '@shared/api';
import { socials } from '@shared/config';

export type RewardStatus = 'locked' | 'available' | 'used' | 'unavailable';
export type RewardReason =
  | 'disabled'
  | 'connect_accounts'
  | 'subscribe'
  | 'verify'
  | 'no_usage'
  | 'used'
  | null;

export interface SocialMembership {
  connected: boolean;
  subscribed: boolean | null;
  url: string;
  username?: string | null;
}

export interface SocialRewardStatus {
  enabled: boolean;
  rewardStatus: RewardStatus;
  telegram: SocialMembership;
  vk: SocialMembership;
  dzenUrl: string;
  usedAt: string | null;
  canUse: boolean;
  reason: RewardReason;
}

interface BackendSocialRewardStatus {
  enabled: boolean;
  reward_status: RewardStatus;
  telegram: SocialMembership;
  vk: SocialMembership;
  dzen_url: string;
  used_at: string | null;
  can_use: boolean;
  reason: RewardReason;
}

/** invite* — личная ссылка: само вступление в канал подтверждает аккаунт. bot* — для уже подписанных. */
export interface TelegramLinks {
  inviteUrl: string;
  inviteAppUrl: string;
  botUrl: string;
  botAppUrl: string;
}

interface BackendTelegramLinks {
  invite_url: string;
  invite_app_url: string;
  bot_url: string;
  bot_app_url: string;
}

const basePath = '/me/social-reward';

function mapStatus(data: BackendSocialRewardStatus): SocialRewardStatus {
  return {
    enabled: data.enabled,
    rewardStatus: data.reward_status,
    telegram: { ...data.telegram, url: data.telegram.url || 'https://t.me/app_lomora' },
    vk: { ...data.vk, url: data.vk.url || socials.vk },
    dzenUrl: data.dzen_url || socials.dzen,
    usedAt: data.used_at,
    canUse: data.can_use,
    reason: data.reason,
  };
}

export async function fetchSocialReward(): Promise<SocialRewardStatus | null> {
  try {
    const { data } = await api.get<BackendSocialRewardStatus>(basePath);
    return mapStatus(data);
  } catch (error) {
    // An older backend does not advertise this promotion yet.
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
}

export async function fetchTelegramLinks(): Promise<TelegramLinks> {
  const { data } = await api.post<BackendTelegramLinks>(`${basePath}/connect/telegram`);
  return {
    inviteUrl: data.invite_url,
    inviteAppUrl: data.invite_app_url,
    botUrl: data.bot_url,
    botAppUrl: data.bot_app_url,
  };
}

/** vk.me chat with ?ref=: the first message there tells the community bot who the user is. */
export async function fetchVkChatLink(): Promise<string> {
  const { data } = await api.post<{ url: string }>(`${basePath}/connect/vk/chat`);
  const url = new URL(data.url);
  if (url.protocol !== 'https:') throw new Error('Invalid VK chat URL');
  return url.href;
}

export async function checkSocialReward(): Promise<SocialRewardStatus> {
  const { data } = await api.post<BackendSocialRewardStatus>(`${basePath}/check`);
  return mapStatus(data);
}

export async function useSocialRewardReset(): Promise<SocialRewardStatus> {
  const { data } = await api.post<{ reward: BackendSocialRewardStatus }>(`${basePath}/use`);
  // Quota and billing have their own adapters; refresh both from their endpoints.
  return mapStatus(data.reward);
}

export function socialRewardErrorKey(error: unknown, fallback: string): string {
  if (isAxiosError(error) && error.response?.status === 429) return 'socialReward.rateLimited';
  const code = isAxiosError<{ error?: { code?: string } }>(error) ? error.response?.data?.error?.code : undefined;
  return socialRewardErrorCodeKey(code, fallback);
}

export function socialRewardErrorCodeKey(code: string | null | undefined, fallback: string): string {
  switch (code) {
    case 'EMAIL_NOT_VERIFIED':
      return 'socialReward.emailRequired';
    case 'SOCIAL_REWARD_ACCOUNT_IN_USE':
      return 'socialReward.accountInUse';
    case 'SOCIAL_REWARD_INVALID_STATE':
      return 'socialReward.linkError';
    case 'SOCIAL_REWARD_NO_USAGE':
      return 'socialReward.noUsage';
    case 'SOCIAL_REWARD_NOT_SUBSCRIBED':
      return 'socialReward.notReady';
    case 'SOCIAL_REWARD_DISABLED':
    case 'SOCIAL_REWARD_PROVIDER_UNAVAILABLE':
    case 'SOCIAL_REWARD_STORAGE_UNAVAILABLE':
      return 'socialReward.unavailable';
    default:
      return fallback;
  }
}
